import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { Box, FormControl, Select, MenuItem } from '@mui/material';
import LoadingDots from '../LoadingDots/LoadingDots';
import { toTitleCase } from '../../utils/casing';

const CustomDropdownIcon = () => (
  <span
    className="material-symbols-rounded"
    style={{
      fontWeight: 500,
      fontSize: '1.5rem',
      color: '#757575',
      display: 'inline-block',
      position: 'absolute',
      right: '16px',
      top: '50%',
      transform: 'translateY(-50%)',
      pointerEvents: 'none',
    }}
  >
    arrow_drop_down
  </span>
);

const LOADING_PHRASES_TAIL = [
  'Getting your deals back',
  'Finding the best',
  'Counting up deals',
];

const ResultsHeader = ({
  initialLoadDone,
  sortOption,
  setSortOption,
  headerText,
  loadingText = 'Talking to insurers',
  progress,
  sortOptions,
}) => {
  const loadingPhrases = [loadingText, ...LOADING_PHRASES_TAIL];
  const progressBarRef = useRef(null);
  const lastProgressRef = useRef(0);
  const preTweenRef = useRef(null); // gsap tween handle
  const [preProgress, setPreProgress] = useState(0); // starts at 0

  // Kick off / stop the slow preload up to 20%
  useEffect(() => {
    // Clear any existing tween
    if (preTweenRef.current) {
      preTweenRef.current.kill();
      preTweenRef.current = null;
    }

    if (!initialLoadDone) {
      const obj = { v: preProgress }; // continue from current preProgress if effect re-runs
      preTweenRef.current = gsap.to(obj, {
        v: 0.2, // 20%
        duration: 12, // slow creep; adjust to taste
        ease: 'none',
        onUpdate: () => setPreProgress(obj.v),
      });
    } else {
      // Snap the preload phase complete when API returns
      setPreProgress(0.2);
    }

    return () => {
      if (preTweenRef.current) {
        preTweenRef.current.kill();
        preTweenRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLoadDone]);

  useEffect(() => {
    if (!progressBarRef.current) return;

    const p = Math.max(0, Math.min(1, Number(progress) || 0));
    const shouldUseMapped = initialLoadDone || p > 0; // start mapping as soon as quotes begin

    // Map:
    // - Preload: creep 0..0.2 while no quotes yet
    // - Mapped:  0.2 + 0.8 * progress once quotes arrive (or when done)
    const target = shouldUseMapped
      ? Math.min(1, 0.2 + 0.8 * p)
      : Math.min(0.2, preProgress);

    // Monotonic within this component
    const next = Math.max(target, lastProgressRef.current);
    lastProgressRef.current = next;

    gsap.killTweensOf(progressBarRef.current);
    gsap.to(progressBarRef.current, {
      width: `${next * 100}%`,
      duration: 0.6,
      ease: 'power2.out',
    });
  }, [initialLoadDone, progress, preProgress]);

  // Ensure the bar starts at 0 width on first render
  useEffect(() => {
    if (progressBarRef.current) {
      gsap.set(progressBarRef.current, { width: '0%' });
      lastProgressRef.current = 0;
    }
  }, []);

  // Phrase cycling
  const [phraseIndex, setPhraseIndex] = useState(0);
  const headingRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (headingRef.current) gsap.set(headingRef.current, { opacity: 1 });

    if (initialLoadDone) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const runFadeCycle = () => {
      if (!headingRef.current) return;
      const tl = gsap.timeline();
      tl.to(headingRef.current, {
        opacity: 0,
        duration: 0,
        ease: 'power2.out',
      })
        .add(() => {
          setPhraseIndex((i) => (i + 1) % loadingPhrases.length);
        })
        .to(headingRef.current, {
          opacity: 1,
          duration: 0.25,
          ease: 'power2.in',
        });
    };

    intervalRef.current = setInterval(runFadeCycle, 5000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [initialLoadDone]);

  const HippoAvatar =
    'https://assets.askhippo.co.za/avatars/v2/Point-and-grunt-v2.gif';

  return (
    <Box
      sx={{
        mx: 'auto',
        pt: '0.0625rem',
        position: 'relative',
        px: '32px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            component="img"
            src={HippoAvatar}
            alt="HippoAvatar"
            sx={{
              width: '120px',
              height: '120px',
              mr: 2,
              objectFit: 'contain',
            }}
          />

          {/* Heading */}
          <Box
            ref={headingRef}
            sx={{
              fontFamily: 'Inter',
              fontWeight: initialLoadDone ? 400 : 700,
              fontSize: '2.125rem',
              lineHeight: '120%',
              color: '#1E354E',
              transition: 'opacity 0.35s',
              whiteSpace: 'normal',
            }}
          >
            {initialLoadDone ? (
              <span dangerouslySetInnerHTML={{ __html: headerText }} />
            ) : (
              <span>
                {loadingPhrases[phraseIndex]}
                <LoadingDots variant="header" />
              </span>
            )}
          </Box>
        </Box>

        {initialLoadDone && sortOptions?.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <FormControl
              variant="outlined"
              sx={{
                width: '261px',
                backgroundColor: '#FFFFFF',
                border: '1px solid #D1D1D1',
                borderRadius: '1.125rem',
                '& .MuiInputBase-root': {
                  height: '100%',
                  pl: 0,
                  alignItems: 'center',
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: '1rem',
                  lineHeight: '100%',
                  color: '#757575',
                  borderRadius: '1.125rem',
                },
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              }}
            >
              <Select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                sx={{
                  pt: '16px',
                  pb: '16px',
                  width: '100%',
                  '.MuiSelect-select': {
                    display: 'flex',
                    justifyContent: 'left',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    padding: 0,
                    ml: '12px',
                  },
                  '.MuiSelect-icon': {
                    fontSize: '1.375rem',
                    color: '#757575',
                    marginRight: '1rem',
                    pointerEvents: 'none',
                  },
                }}
                IconComponent={CustomDropdownIcon}
                displayEmpty
              >
                {sortOptions &&
                  sortOptions.length > 0 &&
                  sortOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {toTitleCase(option.label)}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>

      {/* Progress Bar */}
      <Box
        sx={{
          height: '4px',
          backgroundColor: '#E0E0E0',
          borderRadius: '0.25rem',
          mt: 1.5,
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          ref={progressBarRef}
          sx={{
            position: 'absolute',
            height: '100%',
            backgroundColor: '#C9CC37',
            left: 0,
            top: 0,
            width: '0%', // start at 0
          }}
        />
      </Box>
    </Box>
  );
};

export default ResultsHeader;
